import React, { useState, useEffect, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search, Plus, Trash2, Eye, Edit, Send, FileText } from "lucide-react";
import { toast } from "react-toastify";
import { Project } from "../../../types/api";
import ProjectForm from "./ProjectForm";
import { PROJECT_PLANNING_APPROVALS } from "../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermission";
import { useProjectUtilityStore } from "../../../store/utilitySystemsStore";
import { submitProjectForApproval } from "../../../service/api";
import images from "../../../assets/images";
import { useOrgUsersStore } from "../../../store/store";
import { useLocationStore } from "../../../store/masterDataStore";
import ApprovalTimeline from "./ApprovalTimeline";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const ProjectList: React.FC = () => {
  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const gridRef = useRef<AgGridReact>(null);

  const { projects, projectsLoading, fetchProjects, deleteProject } =
    useProjectUtilityStore();
  const { orgUsersMap, fetchOrgUsers } = useOrgUsersStore();
  const { allLocationsMap, fetchAllLocations } = useLocationStore();
  const fetchProjectsData = useCallback(async () => {
    try {
      if (!orgUsersMap.size) await fetchOrgUsers();
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      await fetchProjects(params);
    } catch (error: any) {
      toast.error("Failed to fetch projects");
      console.error("Error fetching projects:", error);
    }
  }, [fetchProjects, statusFilter, typeFilter]);

  useEffect(() => {
    fetchProjectsData();
  }, [fetchProjectsData]);

  const handleCreate = () => {
    setSelectedProject(null);
    setMode("create");
    setIsFormOpen(true);
  };

  const handleEdit = (project: Project) => {
    setSelectedProject(project);
    setMode("edit");
    setIsFormOpen(true);
  };

  const handleView = (project: Project) => {
    setSelectedProject(project);
    setMode("view");
    setIsFormOpen(true);
  };

  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const handleViewTimeline = (project: Project) => {
    setSelectedProject(project);
    setIsTimelineOpen(true);
  };

  const handleDelete = async (project: Project) => {
    if (!window.confirm("Are you sure you want to delete this project?")) {
      return;
    }

    try {
      const id = project.id || project._id;
      if (!id) return;

      await deleteProject(id);
      toast.success("Project deleted successfully");
      fetchProjectsData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to delete project");
      console.error("Error deleting project:", error);
    }
  };

  const handleSubmitForApproval = async (project: Project) => {
    if (!window.confirm("Submit this project for approval?")) {
      return;
    }

    try {
      const id = project.id || project._id;
      if (!id) return;

      if (!project.approvalLevels || project.approvalLevels.length === 0) {
        toast.error("Please add approval levels to the project before submitting for approval. Edit the project to add approval levels.");
        return;
      }

      await submitProjectForApproval(id, project.approvalLevels);
      toast.success("Project submitted for approval successfully");
      fetchProjectsData();
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || "Failed to submit project for approval"
      );
      console.error("Error submitting project:", error);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedProject(null);
    setMode("create");
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchProjectsData();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft":
        return "#9E9E9E";
      case "Pending Approval":
        return "#FF9800";
      case "Approved":
        return "#4CAF50";
      case "In-Progress":
        return "#2196F3";
      case "Completed":
        return "#9C27B0";
      case "Rejected":
        return "#F44336";
      default:
        return "#9E9E9E";
    }
  };

  const filteredProjects: Project[] = projects.filter((project: Project) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        project.name?.toLowerCase().includes(searchLower) ||
        project.locationName?.toLowerCase().includes(searchLower) ||
        project.projectLeadName?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const columnDefs: ColDef[] = [
    {
      headerName: "Project Name",
      field: "name",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: {
        padding: "10px 5px 0 5px",
        textAlign: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    {
      headerName: "Type",
      field: "type",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
    },
    {
      headerName: "Location",
      field: "locationId",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 5px 0 10px", textAlign: "left" },
      cellRenderer: (params: any) => {
        const location = allLocationsMap.get(params.data.locationId);
        return location || "N/A";
      },
    },
    {
      headerName: "Start Date",
      field: "startDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => formatDate(params.value),
    },
    {
      headerName: "End Date",
      field: "endDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => formatDate(params.value),
    },
    {
      headerName: "Project Lead",
      field: "projectLeadId",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        const projectLead = orgUsersMap.get(params.data.projectLeadId);
        return projectLead || "N/A";
      },
    },
    // {
    //   headerName: "Estimated Cost",
    //   field: "estimatedCost",
    //   flex: 1.5,
    //   headerStyle: { backgroundColor: "#e9f5ff" },
    //   cellStyle: { padding: "10px 0 0 0" },
    //   cellRenderer: (params: any) => formatCurrency(params.value || 0),
    // },
    // {
    //   headerName: "Status",
    //   field: "status",
    //   flex: 1.2,
    //   headerStyle: { backgroundColor: "#e9f5ff" },
    //   cellStyle: (params) => {
    //     return {
    //       color: getStatusColor(params.value),
    //       fontWeight: "bold",
    //       padding: "10px 0 0 0",
    //     };
    //   },
    // },
    {
      headerName: "Action",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        const project = params.data as Project;
        return (
          <div className="flex items-center gap-2 justify-center">
            {canRead(PROJECT_PLANNING_APPROVALS) && (
              <button onClick={() => handleView(project)} title="View Project">
                <img className="iconTd" src={images.viewIcon} alt="View" />
              </button>
            )}
            {canUpdate(PROJECT_PLANNING_APPROVALS) &&
              project.status === "Draft" && (
                <button
                  onClick={() => handleEdit(project)}
                  title="Edit Project"
                >
                  <img className="iconTd" src={images.editIcon} alt="Edit" />
                </button>
              )}
            {(project.status === "Draft" || project.status === "Rejected") && (
              <button
                onClick={() => handleSubmitForApproval(project)}
                className="text-purple-600 hover:text-purple-700"
                title="Submit for Approval"
              >
                <Send size={16} />
              </button>
            )}
            {canRead(PROJECT_PLANNING_APPROVALS) &&
              (project.status === "Pending Approval" ||
                project.status === "Approved") && (
                <button
                  onClick={() => handleViewTimeline(project)}
                  className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                  title="View Approval Timeline"
                >
                  <FileText className="w-4 h-4" />
                </button>
              )}
            {canDelete(PROJECT_PLANNING_APPROVALS) &&
              project.status === "Draft" && (
                <button
                  onClick={() => handleDelete(project)}
                  title="Delete Project"
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={18} />
                </button>
              )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          Project Planning & Approvals
        </h1>
        {canCreate(PROJECT_PLANNING_APPROVALS) && (
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-lg hover:bg-[#0d8f8c] transition-colors"
            onClick={handleCreate}
          >
            <Plus size={18} />
            New Project
          </button>
        )}
      </div>

      <div className="bg-[#ffffff] w-[100%] mb-4">
        <div className="px-3 py-3">
          <div className="relative flex justify-between items-center">
            <div className="flex w-full justify-start items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by Project Name, Location, or Project Lead"
                  className="border-2 border-[#4f5762] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border-2 border-[#4f5762] px-4 py-2 rounded font-semibold text-[#4e545d]"
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Pending Approval">Pending Approval</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="In-Progress">In-Progress</option>
                <option value="Completed">Completed</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="border-2 border-[#4f5762] px-4 py-2 rounded font-semibold text-[#4e545d]"
              >
                <option value="">All Types</option>
                <option value="Construction">Construction</option>
                <option value="Renovation">Renovation</option>
              </select>
              {(statusFilter || typeFilter || searchTerm) && (
                <button
                  onClick={() => {
                    setStatusFilter("");
                    setTypeFilter("");
                    setSearchTerm("");
                  }}
                  className="px-4 py-2 border-2 border-[#4f5762] rounded hover:bg-gray-100 font-semibold text-[#4e545d]"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#ffffff] w-[100%]">
        <div className="ag-theme-alpine mt-4">
          {projectsLoading ? (
            <div className="text-center py-4">Loading projects...</div>
          ) : (
            <AgGridReact
              ref={gridRef}
              rowData={filteredProjects}
              columnDefs={columnDefs}
              rowHeight={50}
              headerHeight={65}
              paginationPageSize={10}
              pagination={true}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              getRowStyle={(params) => {
                return {
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginBottom: "8px",
                  borderRadius: "4px",
                  backgroundColor: "white",
                };
              }}
            />
          )}
        </div>
      </div>

      {isFormOpen && (
        <ProjectForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          project={selectedProject}
          mode={mode}
          onSuccess={handleFormSuccess}
        />
      )}

      {isTimelineOpen && selectedProject && (
        <ApprovalTimeline
          isOpen={isTimelineOpen}
          onClose={() => {
            setIsTimelineOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}
    </div>
  );
};

export default ProjectList;
